import React, { useState, useRef, useCallback, useMemo } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CORE_ORDER = ['B', 'K', 'Zh', 'D', 'Z', 'I', 'L', 'G', 'M', 'A', 'V', 'E', 'HRT', 'Man', 'SUN'];

const DEFAULT_POSITIONS = {
  B: { x: 300, y: 30 }, K: { x: 300, y: 100 }, Zh: { x: 300, y: 165 },
  D: { x: 175, y: 245 }, Z: { x: 425, y: 245 },
  I: { x: 85, y: 285 }, L: { x: 515, y: 285 },
  G: { x: 300, y: 315 }, M: { x: 300, y: 415 },
  A: { x: 25, y: 305 }, V: { x: 575, y: 305 },
  E: { x: 300, y: 560 },
  HRT: { x: 300, y: 240 }, Man: { x: 300, y: 370 },
  SUN: { x: 520, y: 50 },
};

const DEFAULT_COLORS = {
  B: '#a855f7', K: '#6366f1', Zh: '#3b82f6',
  D: '#4f46e5', Z: '#4f46e5', I: '#0ea5e9', L: '#0ea5e9',
  G: '#22c55e', M: '#eab308', A: '#f97316', V: '#f97316', E: '#ef4444',
  HRT: '#10b981', Man: '#f59e0b', SUN: '#fbbf24',
};

const DEFAULT_LABELS_LETTER = {
  B: 'Б', D: 'Д', Zh: 'Ж', Z: 'З', I: 'И', L: 'Л',
  G: 'Г', M: 'М', A: 'А', K: 'К', V: 'В', E: 'Е',
  HRT: 'HRT', Man: 'Man', SUN: '☀',
};

const PositionEditor = ({
  positions,
  colors,
  labels,
  connections,
  nodeSizes,
  globalSphereSize,
  backgroundLayers,
  onChange,
}) => {
  const svgRef = useRef(null);
  const [dragging, setDragging] = useState(null);

  const allIds = useMemo(() => {
    const fromLabels = labels ? Object.keys(labels) : [];
    // Always show all CORE spheres in the editor so the user can drag them,
    // even when the config (e.g. student page) defined labels only for a subset.
    const merged = new Set([...CORE_ORDER, ...fromLabels]);
    return Array.from(merged);
  }, [labels]);

  const getPos = (id) => (positions && positions[id]) || DEFAULT_POSITIONS[id] || { x: 300, y: 350 };
  const getColor = (id) => (colors && colors[id]) || DEFAULT_COLORS[id] || '#a855f7';
  const getLabel = (id) => {
    const fromCfg = labels && labels[id] && labels[id].letter;
    if (fromCfg && String(fromCfg).trim() !== '') return fromCfg;
    return DEFAULT_LABELS_LETTER[id] || id;
  };
  const globalSize = globalSphereSize || 22;
  const getSize = (id) => {
    const override = nodeSizes && nodeSizes[id];
    return (override && override > 0) ? override : globalSize;
  };

  const getSVGPoint = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    pt.x = clientX;
    pt.y = clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
    return {
      x: Math.round(Math.max(15, Math.min(585, svgP.x))),
      y: Math.round(Math.max(15, Math.min(685, svgP.y))),
    };
  }, []);

  const handlePointerDown = (nodeId, e) => {
    e.preventDefault();
    setDragging(nodeId);
  };

  const handlePointerMove = useCallback((e) => {
    if (!dragging) return;
    e.preventDefault();
    const point = getSVGPoint(e);
    if (point) {
      const newPositions = { ...(positions || {}) };
      newPositions[dragging] = point;
      onChange(newPositions);
    }
  }, [dragging, positions, onChange, getSVGPoint]);

  const handlePointerUp = useCallback(() => setDragging(null), []);

  const activeConns = connections || [];
  const layers = backgroundLayers || [];

  return (
    <div className="admin-section">
      <h3 className="admin-section-title">Sfera yerlərini dəyişin</h3>
      <p className="admin-section-desc">Sferaları sürükləyib yeni yerə qoyun. Yüklənmiş arxa fon layer-ləri görünür.</p>

      <div className="position-editor-container">
        <svg
          ref={svgRef}
          viewBox="0 0 600 700"
          className="position-editor-svg"
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerUp}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
        >
          <defs>
            <pattern id="pe-grid" width="30" height="30" patternUnits="userSpaceOnUse">
              <path d="M 30 0 L 0 0 0 30" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="600" height="700" fill="url(#pe-grid)" />

          {/* Background layers — render with fit_mode to match the main page exactly */}
          {layers.map((layer, i) => {
            const fit = layer.fit_mode || 'cover';
            // Map main-page CSS objectFit to SVG preserveAspectRatio
            // cover -> "xMidYMid slice" (fill, may crop)
            // contain/fit -> "xMidYMid meet" (fit inside, letterbox)
            // stretch/fill -> "none" (distort)
            // natural/none -> "xMidYMid meet" (don't distort)
            let par = 'xMidYMid slice';
            if (fit === 'contain' || fit === 'fit') par = 'xMidYMid meet';
            else if (fit === 'stretch' || fit === 'fill') par = 'none';
            else if (fit === 'natural' || fit === 'none') par = 'xMidYMid meet';
            return (
              <g key={`bg-${i}`}
                 transform={`translate(${layer.offset_x || 0}, ${layer.offset_y || 0}) translate(300, 350) scale(${layer.scale || 1}) translate(-300, -350)`}
                 opacity={(layer.opacity ?? 1) * 0.7}>
                <image
                  href={`${BACKEND_URL}/api/uploads/${layer.filename}`}
                  x="0" y="0" width="600" height="700"
                  preserveAspectRatio={par}
                />
              </g>
            );
          })}

          {/* Subtle silhouette if no bg layers */}
          {layers.length === 0 && (
            <g opacity="0.08" stroke="rgba(147,197,253,1)" strokeWidth="0.8" fill="none">
              <circle cx="300" cy="85" r="30" />
              <line x1="300" y1="115" x2="300" y2="145" />
              <line x1="180" y1="165" x2="420" y2="165" />
              <line x1="240" y1="165" x2="230" y2="420" />
              <line x1="360" y1="165" x2="370" y2="420" />
              <line x1="180" y1="165" x2="30" y2="310" />
              <line x1="420" y1="165" x2="570" y2="310" />
              <line x1="260" y1="420" x2="220" y2="650" />
              <line x1="340" y1="420" x2="380" y2="650" />
            </g>
          )}

          {/* Connection preview */}
          {activeConns.map(([fromId, toId], i) => {
            const from = getPos(fromId);
            const to = getPos(toId);
            return (
              <line key={`c-${i}`}
                x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                stroke="rgba(147,197,253,0.25)" strokeWidth="1.2" strokeLinecap="round"
              />
            );
          })}

          {/* Draggable nodes */}
          {allIds.map(id => {
            const pos = getPos(id);
            const color = getColor(id);
            const size = Math.max(12, Math.min(50, getSize(id) * 0.85));
            const isDragging = dragging === id;

            return (
              <g
                key={id}
                onMouseDown={(e) => handlePointerDown(id, e)}
                onTouchStart={(e) => handlePointerDown(id, e)}
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                data-testid={`position-node-${id}`}
              >
                {isDragging && (
                  <circle cx={pos.x} cy={pos.y} r={size * 1.6} fill={color} opacity="0.15" />
                )}
                <circle cx={pos.x} cy={pos.y} r={size} fill={color} opacity="0.85" stroke="white" strokeWidth="1" strokeOpacity="0.3" />
                <text x={pos.x} y={pos.y} textAnchor="middle" dominantBaseline="central"
                  fill="white" fontSize={Math.max(11, size * 0.7)} fontWeight="800" fontFamily="'Cinzel', serif"
                  stroke="rgba(0,0,0,0.85)" strokeWidth="2.5" strokeLinejoin="round"
                  paintOrder="stroke fill"
                  style={{ pointerEvents: 'none' }}>
                  {getLabel(id)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

export default PositionEditor;
